import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import toast, { Toaster } from "react-hot-toast";
import { FiUsers, FiSearch, FiX, FiSave, FiSettings, FiShield } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const STEP_KEYS = [
  "connectZetaId",
  "joinTelegram",
  "identityConfirmation",
  "documentUpload",
  "livenessCheck",
  "trustedCircle",
  "walletMapping",
  "finalReview",
];

const STATUS_OPTIONS = ["completed", "skipped", "pending", "locked"];

const STATUS_COLOR = {
  completed: "bg-green-50 text-green-600",
  skipped: "bg-gray-100 text-gray-500",
  pending: "bg-amber-50 text-amber-600",
  locked: "bg-red-50 text-red-500",
};

const itemsPerPage = 20;

const emptyOverrideForm = { status: "completed", adminNote: "" };

const KYCUsers = () => {
  const token = localStorage.getItem("token");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [countryFilter, setCountryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const [selectedUser, setSelectedUser] = useState(null);
  const [stepKey, setStepKey] = useState(STEP_KEYS[0]);
  const [overrideForm, setOverrideForm] = useState(emptyOverrideForm);
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async (page, country) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit: itemsPerPage });
      if (country) params.set("country", country);
      const res = await fetch(`${baseUrl}/kyc/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (res.ok && result.status) {
        setUsers(result.data || []);
        setTotalUsers(result.pagination?.total || 0);
      } else {
        setError(result.message || "Failed to fetch KYC users");
      }
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(currentPage, countryFilter); }, [fetchUsers, currentPage, countryFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers(1, countryFilter.trim().toUpperCase());
  };

  const handleClear = () => {
    setCountryFilter("");
    setCurrentPage(1);
    fetchUsers(1, "");
  };

  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage < maxVisiblePages - 1) startPage = Math.max(1, endPage - maxVisiblePages + 1);

  const openManage = (user) => {
    setSelectedUser(user);
    setStepKey(STEP_KEYS[0]);
    setOverrideForm(emptyOverrideForm);
  };

  const closeManage = () => {
    setSelectedUser(null);
    setOverrideForm(emptyOverrideForm);
  };

  const handleSaveOverride = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${baseUrl}/kyc/users/${selectedUser.userId}/step/${stepKey}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: overrideForm.status, adminNote: overrideForm.adminNote }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success("Step status updated!");
        closeManage();
        fetchUsers(currentPage, countryFilter);
      } else {
        toast.error(result.message || "Failed to update step");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading & Sub-navigation Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#ffffe0] flex items-center justify-center">
                <FiUsers size={20} className="text-[#999900]" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">KYC Users</h1>
                <p className="text-xs text-gray-500 mt-0.5">Track and override user KYC verification progress</p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Sub-navigation Tabs */}
              <div className="flex items-center gap-1.5 bg-gray-200/80 p-1 rounded-xl">
                <Link
                  to="/kyc"
                  className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:text-black hover:bg-white/80 transition flex items-center gap-1.5"
                >
                  <FiShield size={13} />
                  <span>KYC Steps</span>
                </Link>
                <Link
                  to="/kyc-users"
                  className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-[#ffff00] text-black shadow-xs flex items-center gap-1.5"
                >
                  <FiUsers size={13} />
                  <span>KYC Users List</span>
                </Link>
              </div>

              <div className="bg-white border border-gray-100 shadow-sm rounded-xl px-4 py-1.5 text-center hidden sm:block">
                <p className="text-[10px] uppercase font-bold text-gray-400">Total Users</p>
                <p className="text-lg font-black text-gray-800">{totalUsers.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Filter bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Country code (e.g. IN)"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] uppercase"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] text-black text-sm font-medium px-4 py-2.5 rounded-lg transition"
            >
              <FiSearch size={14} /> Search
            </button>
            {countryFilter && (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                <FiX size={14} /> Clear
              </button>
            )}
          </form>

          {/* Content */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex justify-center items-center py-24">
              <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5 text-sm">{error}</div>
          ) : users.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-24 text-gray-400">
              <FiUsers size={32} className="text-gray-300 mb-3" />
              <p className="text-sm">No KYC users found.</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                        <th className="px-5 py-3">User</th>
                        <th className="px-5 py-3">Zeta ID</th>
                        <th className="px-5 py-3">Country</th>
                        <th className="px-5 py-3">Progress</th>
                        <th className="px-5 py-3 text-center w-32">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {users.map((user) => {
                        const pct = user.totalSteps ? Math.round((user.stepsCompleted / user.totalSteps) * 100) : 0;
                        return (
                          <tr key={user.userId} className="hover:bg-gray-50 transition">
                            <td className="px-5 py-3.5">
                              <div className="font-medium text-gray-800">{user.name}</div>
                              <div className="text-xs text-gray-400">ID: {user.userId}</div>
                            </td>
                            <td className="px-5 py-3.5 text-gray-600">{user.zetaId}</td>
                            <td className="px-5 py-3.5">
                              <span className="inline-block bg-[#ffffe0] text-[#999900] text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                {user.country}
                              </span>
                            </td>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2 min-w-[140px]">
                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#ffff00] rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-gray-500 whitespace-nowrap">{user.stepsCompleted}/{user.totalSteps}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <button
                                onClick={() => openManage(user)}
                                className="flex items-center gap-1 text-xs text-amber-500 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition mx-auto"
                              >
                                <FiSettings size={12} /> Manage Steps
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 items-center">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &laquo;
                  </button>
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => setCurrentPage(pg)}
                      className={`px-4 py-2 border rounded-lg text-sm transition ${
                        currentPage === pg ? "bg-[#ffff00] text-black border-[#ffff00]" : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      {pg}
                    </button>
                  ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    className="px-3 py-2 border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 text-sm"
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Manage Steps Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeManage}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-base font-semibold text-gray-800">Manage KYC Step</h3>
              <button onClick={closeManage} className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <p className="text-xs text-gray-400 mb-5">{selectedUser.name} &middot; {selectedUser.zetaId}</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Step</label>
                <select
                  value={stepKey}
                  onChange={(e) => setStepKey(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white"
                >
                  {STEP_KEYS.map((key) => (
                    <option key={key} value={key}>{key}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                <select
                  value={overrideForm.status}
                  onChange={(e) => setOverrideForm({ ...overrideForm, status: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] bg-white"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLOR[overrideForm.status]}`}>
                  {overrideForm.status}
                </span>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Admin Note</label>
                <textarea
                  rows={3}
                  placeholder="Reason for this override..."
                  value={overrideForm.adminNote}
                  onChange={(e) => setOverrideForm({ ...overrideForm, adminNote: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffff00] resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveOverride}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-60 text-black text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {saving ? "Saving..." : "Save Override"}
              </button>
              <button onClick={closeManage} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCUsers;
